import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { BsSearch, BsPeople, BsCalendarCheck, BsGraphUp } from "react-icons/bs";
import { HiUserAdd } from "react-icons/hi";
import { ApiContext } from "../../../context/MyContext";
import logo from "../../../assets/Logo_transparent.png";
import { Toaster } from "../../../common/Toaster/Toaster";
import { fetchAllUserDetails } from "../../../service/SubscriptionWellDhanService";
import { AnalyticsDashboard } from "./AnalyticsDashboard";
const DETAILED_DEPTS = ["executive", "hr", "marketing"];

const InfoChip = ({ icon, value, label }) => (
  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
    <span>{icon}</span>
    <span className="text-base font-bold">{value}</span>
    <span className="text-xs text-gray-500">{label}</span>
  </div>
);

export const HomeDashboard = () => {
  const { empName, empDept } = useContext(ApiContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [usersData, setUsersData] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);
  const[toastType, setToastType] = useState("info");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const deptKey = (empDept || "").trim().toLowerCase();
  const showDetailed = DETAILED_DEPTS.includes(deptKey);
  const isSales = deptKey === "sales";
  const isExecutive = deptKey === "executive";

  // --- Fetch All Users (skipped for executive - they use AnalyticsDashboard) ---
  useEffect(() => {
    if (isExecutive) return;
    setLoading(true);
    const EVENT_TYPE_ID = "4850387a-8838-45d5-8c65-dce9d1f86de6"; // Replace this accordingly
    fetchAllUserDetails(EVENT_TYPE_ID)
      .then(res => {
        if (res.success) {
          setUsersData(res.data?.users || []);
        } else {
          setToastMessage(res.message);
          setToastType("error");
        }
      })
      .catch(() => {
        setToastMessage("Error fetching users");
        setToastType("error");
      })
      .finally(() => setLoading(false));
  }, [isExecutive]);

useEffect(() => {
    if (searchTerm) {
      setFilteredUsers(
        usersData.filter((user) =>
          `${user.user_first_name} ${user.user_last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredUsers([]);
    }
  }, [searchTerm, usersData]);

const handleSearch = (e) => {
  e.preventDefault();
  const matches = usersData.filter(
  (user) =>
    `${user.user_first_name} ${user.user_last_name}`.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
);

  if (matches.length === 1) {
    navigate(`/dashboard/${matches[0].user_id}`);
  } else if (matches.length > 1) {
    setFilteredUsers(matches); // Show list if multiple
    setToastMessage("Multiple users found, select one from list.");
    setToastType("info");
  } else {
    setToastMessage("User not found");
    setToastType("error");
  }
};


  // --- DETAILED ORG STATS ---
  const totalUsers = usersData.length;
  const usersWithPlan = usersData.filter(u => u.current_plan_name);
  const activeUsers = usersWithPlan.length;
  const withPlanCoachAssigned = usersWithPlan.filter(
    u => u.diet_coach_id || u.workout_coach_id
  ).length;
  const withPlanCoachNotAssigned = usersWithPlan.filter(
    u => !u.diet_coach_id && !u.workout_coach_id
  ).length;
  const recentUsers = usersData.filter(u => {
    if (!u.user_joining_date) return false;
    const daysAgo = (Date.now() - new Date(u.user_joining_date).getTime()) / (1000 * 3600 * 24);
    return daysAgo <= 30;
  }).length;

  // Executive role gets the full analytics dashboard
  if (isExecutive) {
    return <AnalyticsDashboard />;
  }

  return (
    <div className="flex w-full min-h-screen">
      <div className="w-full flex flex-col items-center relative px-4">
        {/* Watermark */}
        <img
          src={logo}
          alt="watermark"
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10 pointer-events-none select-none z-0"
          style={{
            width: "65vw", maxWidth: 800, minWidth: 260, height: "auto",
          }}
          draggable={false}
        />
        <div className="relative z-10 flex flex-col items-center w-full mt-32">
          <h2 className="text-2xl md:text-3xl font-semibold text-orange-primary mb-6 w-full text-center">
            {empName ? `Welcome, ${empName}!` : "Welcome!"}
          </h2>
          {loading ? (
            <div className="mt-16 text-lg text-gray-500">Loading...</div>
          ) : (
            <>
              {showDetailed && (
                <div className="flex flex-wrap gap-3 w-full max-w-6xl mb-8 justify-center">
                  {/* For sales, only show Total Users */}
                  {isSales ? (
                    <InfoChip icon={<BsPeople className="text-orange-primary" />} value={totalUsers} label="Total Users" />
                  ) : (
                    <>
                      <InfoChip icon={<BsPeople className="text-orange-primary" />} value={totalUsers} label="Total Users" />
                      <InfoChip icon={<BsCalendarCheck className="text-green" />} value={activeUsers} label="Users With Plan" />
                      <InfoChip icon={<BsGraphUp className="text-yellow-400" />} value={withPlanCoachAssigned} label="Planned Users & Coach is Assigned" />
                      <InfoChip icon={<BsGraphUp className="text-gray-400" />} value={withPlanCoachNotAssigned} label="Planned Users, No Coach Assigned" />
                      <InfoChip icon={<HiUserAdd className="text-gray-800" />} value={recentUsers} label="Joined Last 30d" />
                    </>
                  )}
                </div>
              )}
              <div className="w-full max-w-xl mb-6">
                <form
                  className="relative bg-white border border-gray-200 rounded-xl shadow-md flex items-center px-4 py-3 focus-within:border-orange-primary transition duration-200"
                  onSubmit={handleSearch}
                  style={{ minHeight: 60 }}
                >
                  <BsSearch size={22} className="text-gray-400 mr-3 ml-1" />
                  <input
                    type="text"
                    id="simple-search"
                    className="flex-1 bg-transparent outline-none text-gray-900 text-base placeholder-gray-400"
                    placeholder="Search users by full name..."
                    required
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="ml-2 px-4 py-2 rounded-lg bg-orange-primary text-white font-semibold shadow-sm hover:bg-dark-orange transition"
                  >
                    Search
                  </button>
                  {filteredUsers.length > 0 && (
                    <ul className="absolute left-0 top-[110%] w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-2 max-h-52 overflow-y-auto z-20 animate-fadeIn">
                      {filteredUsers.map((user) => (
                        <li
                          key={user.user_id}
                          className="px-4 py-2 hover:bg-light-orange cursor-pointer text-left transition"
                          onClick={() => navigate(`/dashboard/${user.user_id}`)}
                        >
                        {user.user_first_name} {user.user_last_name}
                        </li>
                      ))}
                    </ul>
                  )}
                </form>
              </div>
            </>
          )}
        </div>
      </div>
      {toastMessage && <Toaster message={toastMessage} type ={toastType}/>}
    </div>
  );
};
